/**
 * The next-step queue for "My inventions" (Story #5648 build v2, figures S4
 * and S4b's banner: "NEXT AT MY INVENTIONS · REVIEW OVERLAP … 2 more items in
 * your queue — See queue · 1 snoozed · show").
 *
 * EVERY ITEM IS DERIVED FROM A REAL OWN-SCOPE PAYLOAD. Nothing here is a
 * constant, and nothing is invented: the overlap item exists only when the
 * overlap scan actually formed a group, the fund-once item only when one
 * funding programme is genuinely named by more than one of your inventions,
 * the documents item only when an invention you own really has none, and so
 * on. When no payload supports any item the caller renders the honest
 * "nothing waiting" form of the banner rather than a filler sentence.
 *
 * "3 steps waiting" in the top-bar rail is the LENGTH OF THIS QUEUE. It is the
 * same number the banner counts down from, so the two can never disagree.
 *
 * Pure on purpose — no fetch, no react — so the wording and the ordering are
 * testable against real captured payloads.
 */
import { overlapTierForScore } from '../../lib/whitespace'
import { OWN_SCOPE_ENTITLEMENT_LINE } from './ownScopeViews'
import type {
  OverlapGroup,
  OverlapResponse,
  OverlapFundingRollup,
  PortfolioTopologyResponse,
  PortfolioPressureResponse,
} from '../../types/whitespace'

export interface OwnNextStepItem {
  /** Stable across reloads — the snooze record keys on it, so it must not
   * contain anything that changes when the same situation is recomputed. */
  id: string
  /** The banner eyebrow's verb: "Next at My inventions · review overlap". */
  verb: string
  headline: string
  /** Where the step comes from and what it does not change. */
  rule: string
  cta: { label: string; to: string }
}

export interface OwnNextStepQueue {
  /** Live items, most important first. Length is "N steps waiting". */
  waiting: OwnNextStepItem[]
  /** Items the reader has snoozed or dropped, still real, just set aside. */
  snoozed: OwnNextStepItem[]
}

/** The durable per-user preference the Snooze / Not now controls write.
 * PUT /innovateflow/preferences {key, value} — the same 4-layer preference
 * store the rest of the app uses, so a snooze survives a reload and a new
 * device, not just this browser tab. */
export const SNOOZE_PREFERENCE_KEY = 'next_step_snoozed'

/** "Not now" — dropped until the situation itself changes and mints a new id. */
export const SNOOZE_DROPPED = 'dropped'

export type SnoozeRecord = Record<string, string>

export const SNOOZE_DAY_OPTIONS = [7, 30] as const

export function snoozeUntil(days: number, now: number = Date.now()): string {
  return new Date(now + days * 24 * 60 * 60 * 1000).toISOString()
}

export function isSnoozed(record: SnoozeRecord, id: string, now: number = Date.now()): boolean {
  const entry = record[id]
  if (!entry) return false
  if (entry === SNOOZE_DROPPED) return true
  const until = Date.parse(entry)
  return !Number.isNaN(until) && until > now
}

/** Only the keys that are still holding something back, so a stale row cannot
 * inflate the "1 snoozed" count for ever. */
export function activeSnoozeCount(record: SnoozeRecord, ids: string[], now: number = Date.now()): number {
  return ids.filter((id) => isSnoozed(record, id, now)).length
}

export interface OwnNextStepInput {
  /** The inventions the caller OWNS (shared-in rows already filtered out). */
  owned: { project_id: string; name: string; document_count?: number }[]
  /** Any overlap payload that carries groups — the enqueueing read or the
   * cache-only one. The banner only ever needs the groups. */
  overlap?: Pick<OverlapResponse, 'groups'>
  funding?: Pick<OverlapFundingRollup, 'funding_mentions_rows'>
  topology?: Pick<PortfolioTopologyResponse, 'frontier_edges'>
  pressure?: Pick<PortfolioPressureResponse, 'competitors' | 'window_days'>
  snoozed?: SnoozeRecord
  now?: number
}

const OVERLAP_RULE = `from your overlap scan · ${OWN_SCOPE_ENTITLEMENT_LINE}`
const FUNDING_RULE = `from your funding scan · ${OWN_SCOPE_ENTITLEMENT_LINE}`
const TOPOLOGY_RULE = `from your topology scan, validated in full space · ${OWN_SCOPE_ENTITLEMENT_LINE}`

/** A group already consolidated or dismissed is finished business and is not a
 * step waiting on anyone. A group with no lifecycle recorded is open. */
const CLOSED_LIFECYCLE = new Set(['consolidated', 'dismissed'])

export function buildOwnNextStepQueue(input: OwnNextStepInput): OwnNextStepQueue {
  const now = input.now ?? Date.now()
  const snoozeRecord = input.snoozed ?? {}
  const items: OwnNextStepItem[] = []

  // 1. Overlap — the figure's own first item. Strongest open group first.
  const openGroups = (input.overlap?.groups ?? []).filter(
    (g) => !CLOSED_LIFECYCLE.has(g.lifecycle_status ?? ''),
  )
  const strongest = openGroups.reduce<OverlapGroup | null>(
    (best, g) => (best === null || g.score > best.score ? g : best),
    null,
  )
  if (strongest) {
    const memberCount = strongest.member_project_ids.length
    const subject = memberCount === 2 ? 'Two of your inventions' : `${memberCount} of your inventions`
    const verdict =
      overlapTierForScore(strongest.score) === 'strong'
        ? 'look like the same work'
        : 'share significant ground'
    items.push({
      id: `overlap:${strongest.group_id}`,
      verb: 'review overlap',
      headline: `${subject} ${verdict} — strongest pair ${strongest.score}.`,
      rule: OVERLAP_RULE,
      cta: { label: 'Open the group →', to: '/projects?view=overlap' },
    })
  }

  // 2. Fund once — a programme genuinely named by more than one invention.
  const duplicateAsks = (input.funding?.funding_mentions_rows ?? []).filter(
    (r) => new Set(r.mentions.map((m) => m.project_id)).size > 1,
  )
  const firstAsk = duplicateAsks[0]
  if (firstAsk) {
    const askCount = new Set(firstAsk.mentions.map((m) => m.project_id)).size
    const programme = [firstAsk.programme, firstAsk.identifier].filter(Boolean).join(' ')
    items.push({
      id: `funding:${firstAsk.programme ?? ''}|${firstAsk.identifier ?? ''}`,
      verb: 'file once',
      headline: `${programme || 'One funding body'} is named by ${askCount} of your inventions — file once, not ${askCount} times.`,
      rule: FUNDING_RULE,
      cta: { label: 'Review the funding asks →', to: '/projects?view=overlap' },
    })
  }

  // 3. An invention with no documents cannot be drawn anywhere — the most
  //    common real reason an atlas looks emptier than the roster.
  const withoutDocuments = input.owned.filter((p) => (p.document_count ?? 0) === 0)
  const firstWithout = withoutDocuments[0]
  if (firstWithout) {
    const n = withoutDocuments.length
    items.push({
      id: `documents:${firstWithout.project_id}`,
      verb: 'add documents',
      headline:
        n === 1
          ? `${firstWithout.name} has no documents yet, so it cannot be mapped.`
          : `${n} of your inventions have no documents yet, so they cannot be mapped.`,
      rule: 'from your own inventions · an invention is drawn from its documents, so one with none has nowhere to sit',
      cta: { label: `Open ${firstWithout.name} →`, to: `/project/${firstWithout.project_id}` },
    })
  }

  // 4. Frontier directions — open ground at the edge of your set.
  const frontierCount = input.topology?.frontier_edges?.length ?? 0
  if (frontierCount > 0) {
    items.push({
      id: 'frontier',
      verb: 'read the frontier',
      headline:
        frontierCount === 1
          ? 'One frontier direction is open at the edge of your set.'
          : `${frontierCount} frontier directions are open at the edge of your set.`,
      rule: TOPOLOGY_RULE,
      cta: { label: 'Open Shape & Strategy →', to: '/projects?view=shape' },
    })
  }

  // 5. Encroachment pressure on what you already watch.
  const competitors = input.pressure?.competitors ?? []
  if (competitors.length > 0) {
    const days = input.pressure?.window_days ?? 90
    items.push({
      id: 'pressure',
      verb: 'check the watch',
      headline:
        competitors.length === 1
          ? `One filer moved into a band you watch in the last ${days} days.`
          : `${competitors.length} filers moved into a band you watch in the last ${days} days.`,
      rule: `from your watch rescan over the last ${days} days · ${OWN_SCOPE_ENTITLEMENT_LINE}`,
      cta: { label: 'Open Watch →', to: '/projects?view=watch' },
    })
  }

  return {
    waiting: items.filter((i) => !isSnoozed(snoozeRecord, i.id, now)),
    snoozed: items.filter((i) => isSnoozed(snoozeRecord, i.id, now)),
  }
}
